import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import blogimage1 from "./../../images/blog/picforasana.png";
import coverWeb from "./../../images/blog/picforasana-1280.webp";
import FAQ from "../../components/FAQs"; // adjust path
import { DEMO_LINK } from "../../components/demoBooking";
import CopyLinkButton from "../../components/CopyLinkButton";

export default function Blog_AiProjectManagement() {
  const title =
    "AI Project Management with BossDrop: Smarter Task Assignment, Fewer Meetings";
  const description =
    "BossDrop AI for project management automates task assignment, status roll-ups, and escalations - so teams ship faster with fewer meetings.";
  const cover = blogimage1;
  const canonicalUrl =
    "https://bossdrop.ai/blog/ai-project-management-with-bossdrop";
  const published = "2025-08-19";
  const readTime = "8 min read";

  const keywords = [
    "BossDrop AI",
    "BossDrop",
    "AI for project management",
    "AI project management",
    "AI workflow automation",
    "autonomous work management",
    "work management",
    "workflow management",
    "project management tool",
    "best task management software",
    "team collaboration tools",
    "productivity software",
  ];

  // --- social share handlers
  const shareUrl = encodeURIComponent(canonicalUrl);
  const shareText = encodeURIComponent(title);
  const onShare = (net) => {
    const map = {
      x: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    document.title = title;
    setCanonical(canonicalUrl);
    setMeta(description);
    setKeywords(keywords.join(", "));
    setOG("og:type", "article");
    setOG("og:title", title);
    setOG("og:description", description);
    setOG("og:url", canonicalUrl);
    setOG("og:image", toAbs(cover));
    setOG("twitter:card", "summary_large_image");
    setOG("twitter:title", title);
    setOG("twitter:description", description);
    setOG("twitter:image", toAbs(cover));

    injectLDJSON("ldjson-blogpost", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: title,
      description,
      datePublished: published,
      dateModified: published,
      author: { "@type": "Organization", name: "BossDrop" },
      publisher: {
        "@type": "Organization",
        name: "BossDrop",
        logo: { "@type": "ImageObject", url: "https://bossdrop.ai/icon.png" },
      },
      image: toAbs(cover),
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      keywords: keywords.join(", "),
      articleSection: "AI Project Management",
    });

    // reveal on scroll
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!("IntersectionObserver" in window) || !els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className="page container">
      {/* Breadcrumbs */}
      <nav
        className="breadcrumbs reveal"
        style={{ "--delay": "0ms" }}
        aria-label="Breadcrumb"
      >
        <Link to="/">Home</Link>
        <span>›</span>
        <Link to="/blog">Blog</Link>
        <span>›</span>
        <span aria-current="page">AI Project Management</span>
      </nav>

      {/* Feature hero (ClickUp-style) */}
      <div className="hero-feature reveal" style={{ "--delay": "40ms" }}>
        <div className="hero-gradient" />
        <div className="hero-grid">
          <div className="hero-card">
            <img
              src={coverWeb}
              alt="BossDrop AI assigning tasks across a project timeline"
            />
          </div>

          <div className="hero-text">
            <div className="pill">PROJECT MANAGEMENT</div>
            <h1 className="h1">{title}</h1>

            <div className="byline">
              <div className="author">
                <div className="avatar" aria-hidden="true">
                  BD
                </div>
                <div>
                  <div className="author-name">BossDrop Team</div>
                  <div className="meta">
                    {new Date(published).toLocaleDateString()} • {readTime}
                  </div>
                </div>
              </div>

              <div className="share">
                <button
                  aria-label="Share on X"
                  onClick={() => onShare("x")}
                  title="Share on X"
                >
                  {iconX()}
                </button>
                <button
                  aria-label="Share on LinkedIn"
                  onClick={() => onShare("linkedin")}
                  title="Share on LinkedIn"
                >
                  {iconLinkedIn()}
                </button>
                <button
                  aria-label="Share on Facebook"
                  onClick={() => onShare("facebook")}
                  title="Share on Facebook"
                >
                  {iconFacebook()}
                </button>
                <CopyLinkButton url={canonicalUrl} />
              </div>
            </div>
          </div>
        </div>

        {/* Purple CTA bar */}
        <div className="hero-cta">
          <span>
            Execute at scale with <strong>BossDrop AI</strong>, your autonomous
            work management layer.
          </span>
          <a
            className="btn btnDemo"
            {...DEMO_LINK}
            aria-label="Book a demo"
          >
            Book a demo
          </a>
        </div>
      </div>

      {/* ---- Article ---- */}
      <article className="article">
        <p className="lede reveal" style={{ "--delay": "80ms" }}>
          <strong>BossDrop</strong> is{" "}
          <strong>AI for project management</strong> - an autonomous layer that
          handles
          <strong> AI workflow automation</strong> (task assignment ✅, nudges
          ✅, escalations ✅) so your teams ship faster with fewer meetings. It
          plugs into your existing <strong>project management tool</strong>{" "}
          stack (Asana/Jira + Slack) and produces decision-ready summaries for
          leaders.
        </p>

        <nav
          className="toc reveal"
          style={{ "--delay": "100ms" }}
          aria-label="Table of contents"
        >
          <a href="#what-is-ai-pm">What is AI project management?</a>
          <a href="#how-it-works">How BossDrop AI works</a>
          <a href="#use-cases">Where teams see wins</a>
          <a href="#feature-checklist">Feature checklist</a>
          <a href="#implementation">Implementation</a>
          <a href="#metrics">Metrics to track</a>
          <a href="#videos">Watch: short videos</a>
          <a href="#faq">FAQ</a>
        </nav>

        <h2
          id="what-is-ai-pm"
          className="reveal"
          style={{ "--delay": "120ms" }}
        >
          What is AI project management?
        </h2>
        <p className="reveal" style={{ "--delay": "140ms" }}>
          <strong>AI project management</strong> applies machine learning to
          scheduling,
          <strong> task management software</strong> workflows, status
          reporting, and risk detection. Instead of manual updates and recurring
          status meetings ❌, an AI agent orchestrates work across tools and
          surfaces the next best actions ✅. Think modern{" "}
          <em>work management</em> + <em>workflow management</em> with fewer
          handoffs and faster outcomes.
        </p>


        <h2 id="how-it-works" className="reveal" style={{ "--delay": "180ms" }}>
          How BossDrop AI works (in practice)
        </h2>
        <ul className="reveal checklist" style={{ "--delay": "200ms" }}>
          <li>
            <strong>Connect tools:</strong> Slack + Asana/Jira. BossDrop ingests
            tasks, messages, owners, and due dates.
          </li>
          <li>
            <strong>Understand context:</strong> Goals, dependencies, blockers
            from tickets & conversations.
          </li>
          <li>
            <strong>Assign intelligently:</strong> Skills, workload, timezone,
            ownership history → proposed owners ✅.
          </li>
          <li>
            <strong>Nudges & escalations:</strong> Reminders, SLA checks, and
            critical-path auto-escalations.
          </li>
          <li>
            <strong>Executive roll-ups:</strong> Noise → <em>decision-ready</em>{" "}
            weekly summaries (risks, trade-offs, KPIs).
          </li>
        </ul>

        {/* Figure 2 (reuse image until you add more assets) */}
        <figure className="figure reveal" style={{ "--delay": "220ms" }}>
          <img
            loading="lazy"
            src={coverWeb}
            alt="Capacity-aware assignment and dependency visualization"
            width="1200"
            height="675"
          />
          <figcaption>
            Capacity-aware assignment with dependency checks keeps your{" "}
            <strong>project tracker</strong> realistic.
          </figcaption>
        </figure>

        <div className="callout info reveal" style={{ "--delay": "240ms" }}>
          <strong>Pro tip:</strong> Start with one cross-functional project.
          Approve your first AI plan, then expand org-wide.
        </div>

        <h2 id="use-cases" className="reveal" style={{ "--delay": "260ms" }}>
          Where teams see immediate wins
        </h2>
        <ul className="reveal checks" style={{ "--delay": "280ms" }}>
          <li>
            ✅ <strong>Sprint planning:</strong> Balanced backlogs & dependency
            checks cut planning time 50–70%.
          </li>
          <li>
            ✅ <strong>Incidents / critical path:</strong> Clear owners + faster
            escalations reduce MTTR and deadline risk.
          </li>
          <li>
            ✅ <strong>Cross-functional launches:</strong> Marketing, product,
            and engineering aligned to shared milestones.
          </li>
          <li>
            ✅ <strong>Steering updates:</strong> Exec-ready briefs replace
            status meetings & slide writing.
          </li>
        </ul>

        <h2
          id="feature-checklist"
          className="reveal"
          style={{ "--delay": "320ms" }}
        >
          Feature checklist vs. status-quo
        </h2>
        <ul className="reveal checklist" style={{ "--delay": "340ms" }}>
          <li>
            ✅ <strong>Autonomous work management</strong> (plan, assign, nudge,
            escalate)
          </li>
          <li>
            ✅ Works with your <strong>project management app</strong>{" "}
            (Asana/Jira) and Slack
          </li>
          <li>
            ✅ <strong>Team collaboration tools</strong> integration, approvals,
            audit trail
          </li>
          <li>
            ✅ <strong>Productivity software</strong> KPIs: cycle time, on-time
            rate, workload balance
          </li>
          <li>❌ Endless status meetings</li>
          <li>❌ Manual multi-tool copy/paste</li>
        </ul>

        <h2
          id="implementation"
          className="reveal"
          style={{ "--delay": "360ms" }}
        >
          Implementation in 30 minutes
        </h2>
        <ol className="reveal numbered" style={{ "--delay": "380ms" }}>
          <li>Connect Slack and Asana/Jira.</li>
          <li>Select projects and owners.</li>
          <li>Set nudges & escalation rules (daily/weekly cadence).</li>
          <li>Review the first assignment plan; approve to launch.</li>
        </ol>

        <h2 id="metrics" className="reveal" style={{ "--delay": "400ms" }}>
          Metrics to track (and improve)
        </h2>
        <ul className="reveal checklist" style={{ "--delay": "420ms" }}>
          <li>⏱️ Cycle time per task</li>
          <li>📅 On-time delivery rate</li>
          <li>⚖️ Workload balance (person/team)</li>
          <li>🕒 Meeting hours per week</li>
          <li>🚨 Escalation count & time-to-resolve</li>
        </ul>

        {/* ---- Relevant videos ---- */}
        {/*         <h2 id="videos" className="reveal" style={{ "--delay": "440ms" }}>
          Watch: short videos on AI project management
        </h2>
        <div className="video-grid reveal" style={{ "--delay": "460ms" }}>
          <div className="video">
            <iframe
              loading="lazy"
              title="Asana AI overview"
              src="https://www.youtube.com/embed/kw6864spgWU"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          <div className="video">
            <iframe
              loading="lazy"
              title="Atlassian Intelligence in Jira"
              src="https://www.youtube.com/embed/ivrcLXHmhI8"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          <div className="video">
            <iframe
              loading="lazy"
              title="Automate your work with Slack"
              src="https://www.youtube.com/embed/1BUg3d4qt1w"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div> */}

        <FAQ
          items={[
            {
              q: "Does the AI replace managers?",
              a: "We aren't going to sugar coat things. We are coming for the middle managers because corporations lose millions every year to the wasteful layers.",
            },
            {
              q: "Will it spam Slack?",
              a: "No - cadence is configurable and roll-ups consolidate noise.",
            },
            {
              q: "Security?",
              a: "Scoped access & least privilege. BossDrop only acts in connected tools.",
            },
          ]}
          reveal={false}
        />

        <div className="cta-row reveal" style={{ "--delay": "560ms" }}>
          <a className="btn" {...DEMO_LINK}>
            Book a demo
          </a>
          <a className="btn btnGhost" href="/how-it-works">
            See how it works
          </a>
        </div>
      </article>
    </section>
  );
}

/* ---------- helpers (complete) ---------- */
function setMeta(content) {
  setOrCreate(
    'meta[name="description"]',
    (el) => el.setAttribute("name", "description"),
    content
  );
}
function setKeywords(content) {
  setOrCreate(
    'meta[name="keywords"]',
    (el) => el.setAttribute("name", "keywords"),
    content
  );
}
function setOG(property, content) {
  if (!content) return;
  const sel = property.startsWith("og:")
    ? `meta[property="${property}"]`
    : `meta[name="${property}"]`;
  setOrCreate(
    sel,
    (el) => {
      property.startsWith("og:")
        ? el.setAttribute("property", property)
        : el.setAttribute("name", property);
    },
    content
  );
}
function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}
function setOrCreate(selector, init, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    init(el);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function injectLDJSON(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}
function toAbs(url) {
  if (!url) return "";
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

/* --- inline SVGs --- */
function iconX() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.9 2H22l-7.7 8.8L22 22h-6.3l-4.9-6-5.6 6H2l8.5-9.4L2 2h6.4l4.4 5.4L18.9 2z"
      />
    </svg>
  );
}
function iconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 8h5v16H0V8zm7.5 0h4.8v2.2h.1c.7-1.2 2.3-2.5 4.7-2.5 5 0 5.9 3.3 5.9 7.6V24h-5V16.4c0-1.8 0-4.1-2.5-4.1s-2.9 2-2.9 4v7.7h-5V8z"
      />
    </svg>
  );
}
function iconFacebook() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12"
      />
    </svg>
  );
}
